import { Bill, Project } from '../types';
import { fetchEntity, fetchEntityList, fetchProjectLookup, writeEntity } from '@/lib/entity-api';
import { trackBillCreated, trackBillUpdated, trackFormSubmission } from '@/lib/posthog-events';

/**
 * Bills data layer. Every read and write goes through the backend API: reads are the generic entity / lookups
 * reads (core/entity-read-routes.js), writes the generic entity API. The company and the author are never sent --
 * the server derives them from the session. The Bills TABLE itself is the server-driven list
 * (hooks/useEntityList.ts); this is for what a screen needs besides it: one bill with its items, the
 * bills / projects of a dropdown, and the writes.
 */

export interface BillLine {
  description: string;
  quantity: number;
  unit_price: number;
  /** the line's BILL AMOUNT: what the vendor billed for it */
  total_price: number;
  order_number: number;
  cost_code_id?: string | null;
  /** the purchase-order line this line covers (what bills billed of each order line adds up through it) */
  purchase_order_item_id?: string | null;
  catalog_item_id?: string | null;
  unit?: string | null;
}

export const BillsService = {
  async getProjects(): Promise<Project[]> {
    // every non-deleted project: a bill may point at a finished one
    return (await fetchProjectLookup({ includeInactive: true })) as unknown as Project[];
  },

  /**
   * The bills of one project with what has been paid on each (`amount_paid`, computed by the server) -- for the
   * Bill dropdown of a payment. NOT for a table.
   */
  async getBillsWithBalance(projectId: string): Promise<Array<Bill & { amount_paid: number }>> {
    return fetchEntityList<any>('bill', { projectId, includeRollups: true });
  },

  // One bill with its items
  /** The bill with what has been paid on it (server-computed): the Bill form's payment context, refreshed after a payment. */
  getBillWithBalance(id: string) {
    return fetchEntity<any>('bill', id, { includeRollups: true });
  },
  async getBill(id: string): Promise<{ bill: Bill; items: any[] } | null> {
    const [bill, items] = await Promise.all([
      fetchEntity<any>('bill', id),
      fetchEntityList<any>('bill_item', { filters: { bill_id: id } }),
    ]);
    return { bill, items: [...items].sort((a, b) => (a.order_number ?? 0) - (b.order_number ?? 0)) };
  },

  async createBill(payload: any): Promise<Bill> {
    return writeEntity('api/entities/bill', 'POST', payload, 'Failed to create bill');
  },

  async updateBill(payload: any): Promise<Bill> {
    const { id, ...changes } = payload as any;
    return writeEntity(`api/entities/bill/${id}`, 'PUT', changes, 'Failed to update bill');
  },

  async deleteBill(id: string): Promise<void> {
    await writeEntity(`api/entities/bill/${id}`, 'DELETE', {}, 'Failed to delete bill');
  },

  /** All of a bill's lines in ONE request (the server replaces the previous ones, never leaving the bill without lines). */
  async replaceBillLines(billId: string, lines: BillLine[]): Promise<void> {
    await writeEntity(`api/entities/bill_item/bulk/${billId}`, 'PUT', { items: lines.map((line) => ({ ...line, bill_id: billId })) }, 'Failed to save the bill lines');
  },

};

export default BillsService;

/**
 * Creates (no `id`) or updates (`id`) the bill itself in ONE request and tracks the outcome. The single save path of the Bills
 * table and of "Create Bill" from a purchase order; the form saves the lines and the files around it.
 */
export async function saveBill(billData: any, who: { companyId: string; userId: string }): Promise<Bill> {
  const isUpdate = !!billData.id;
  try {
    const savedBill = isUpdate ? await BillsService.updateBill(billData) : await BillsService.createBill(billData);
    const tracked = {
      bill_id: savedBill.id,
      bill_number: billData.number,
      bill_type: billData.type,
      vendor_id: billData.vendor_id,
      project_id: billData.project_id,
      status: billData.status,
      total_amount: billData.total_amount,
      company_id: who.companyId,
    };
    if (isUpdate) trackBillUpdated({ ...tracked, updated_by: who.userId });
    else trackBillCreated({ ...tracked, created_by: who.userId });
    trackFormSubmission({ form_type: isUpdate ? 'bill_update' : 'bill_creation', success: true });
    return savedBill;
  } catch (e: any) {
    console.error('Error saving bill:', e);
    trackFormSubmission({ form_type: isUpdate ? 'bill_update' : 'bill_creation', success: false, error_message: e.message || 'Failed to save bill' });
    throw e;
  }
}
